// Minimal, dependency-free ZIP writer (STORE method — no compression).
// Receipts are images/PDFs that are already compressed, so storing them
// verbatim keeps this tiny and avoids pulling in a deflate library.

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) {
      c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
    }
    table[n] = c >>> 0;
  }
  return table;
})();

function crc32(bytes) {
  let crc = 0xFFFFFFFF;
  for (let i = 0; i < bytes.length; i += 1) {
    crc = CRC_TABLE[(crc ^ bytes[i]) & 0xFF] ^ (crc >>> 8);
  }
  return (crc ^ 0xFFFFFFFF) >>> 0;
}

// Decode a data: URL (base64 or URL-encoded) into raw bytes.
export function dataUrlToBytes(dataUrl) {
  const comma = dataUrl.indexOf(',');
  if (comma === -1) throw new Error('Malformed data URL');
  const meta = dataUrl.slice(0, comma);
  const body = dataUrl.slice(comma + 1);
  if (meta.includes('base64')) {
    const bin = atob(body);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
    return bytes;
  }
  return new TextEncoder().encode(decodeURIComponent(body));
}

// files: Array<{ name: string, data: Uint8Array }>  →  Blob (application/zip)
export function createZip(files) {
  const enc = new TextEncoder();
  const chunks = [];
  const central = [];
  let offset = 0;

  for (const file of files) {
    const nameBytes = enc.encode(file.name);
    const data = file.data;
    const crc = crc32(data);
    const size = data.length;

    const local = new Uint8Array(30 + nameBytes.length);
    const ldv = new DataView(local.buffer);
    ldv.setUint32(0, 0x04034b50, true); // local file header signature
    ldv.setUint16(4, 20, true);         // version needed to extract
    ldv.setUint16(6, 0x0800, true);     // flags: UTF-8 filename
    ldv.setUint16(8, 0, true);          // compression: store
    ldv.setUint16(10, 0, true);         // mod time
    ldv.setUint16(12, 0, true);         // mod date
    ldv.setUint32(14, crc, true);
    ldv.setUint32(18, size, true);      // compressed size
    ldv.setUint32(22, size, true);      // uncompressed size
    ldv.setUint16(26, nameBytes.length, true);
    ldv.setUint16(28, 0, true);         // extra field length
    local.set(nameBytes, 30);
    chunks.push(local, data);

    const cen = new Uint8Array(46 + nameBytes.length);
    const cdv = new DataView(cen.buffer);
    cdv.setUint32(0, 0x02014b50, true); // central dir header signature
    cdv.setUint16(4, 20, true);         // version made by
    cdv.setUint16(6, 20, true);         // version needed
    cdv.setUint16(8, 0x0800, true);     // flags: UTF-8
    cdv.setUint16(10, 0, true);         // compression: store
    cdv.setUint16(12, 0, true);         // mod time
    cdv.setUint16(14, 0, true);         // mod date
    cdv.setUint32(16, crc, true);
    cdv.setUint32(20, size, true);
    cdv.setUint32(24, size, true);
    cdv.setUint16(28, nameBytes.length, true);
    cdv.setUint16(30, 0, true);         // extra length
    cdv.setUint16(32, 0, true);         // comment length
    cdv.setUint16(34, 0, true);         // disk number start
    cdv.setUint16(36, 0, true);         // internal attrs
    cdv.setUint32(38, 0, true);         // external attrs
    cdv.setUint32(42, offset, true);    // relative offset of local header
    cen.set(nameBytes, 46);
    central.push(cen);

    offset += local.length + data.length;
  }

  const centralSize = central.reduce((s, c) => s + c.length, 0);
  const centralOffset = offset;

  const eocd = new Uint8Array(22);
  const edv = new DataView(eocd.buffer);
  edv.setUint32(0, 0x06054b50, true);   // end of central dir signature
  edv.setUint16(4, 0, true);            // disk number
  edv.setUint16(6, 0, true);            // disk with central dir
  edv.setUint16(8, files.length, true); // entries on this disk
  edv.setUint16(10, files.length, true);// total entries
  edv.setUint32(12, centralSize, true);
  edv.setUint32(16, centralOffset, true);
  edv.setUint16(20, 0, true);           // comment length

  return new Blob([...chunks, ...central, eocd], { type: 'application/zip' });
}
